import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import i18n from "i18next";
import { useTranslation } from "react-i18next";
import { lazy, Suspense, useState } from "react";
import {
  ArrowLeft,
  ArrowUpRight,
  Copy,
  Check,
  Edit3,
  History,
  Key,
  Pencil,
  Trash2,
  Undo2,
} from "lucide-react";
import {
  useDeployments,
  useDeploymentHistory,
  useHeldDeploymentMetrics,
  useDeleteDeployment,
  useRollbackDeployment,
  useDeploymentHaltHistory,
} from "@/features/deployments/hooks/useDeployments";
import { useAppAccess } from "@/features/apps/hooks/useApps";
import type { AppAccess } from "@/features/apps/lib/capabilities";
import { EditReleaseDialog } from "@/features/deployments/components/EditReleaseDialog";
import { PromoteDialog } from "@/features/deployments/components/PromoteDialog";
import { RenameDeploymentDialog } from "@/features/deployments/components/RenameDeploymentDialog";
import { DeploymentMetricsPanel } from "@/features/deployments/components/DeploymentMetricsPanel";
import { HaltHistoryPanel } from "@/features/deployments/components/HaltHistoryPanel";
import { shouldShowMetricsPanel } from "@/features/deployments/lib/metrics";
import {
  isAutoHalted,
  isReleaseHalted,
  shouldRenderHaltHistoryPanel,
} from "@/features/deployments/lib/halt-history";
import {
  isPinnedRollbackApplied,
  previousReleaseLabel,
} from "@/features/deployments/lib/rollback-target";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { formatRelativeTime } from "@/lib/formatters";
import { isUncertainWriteTimeout } from "@/lib/api/client";

const DeploymentHistoryCharts = lazy(() =>
  import("@/features/deployments/components/DeploymentHistoryCharts").then((mod) => ({
    default: mod.DeploymentHistoryCharts,
  })),
);
import { ApiErrorBanner } from "@/components/ui/ApiErrorBanner";
import { PageLoader } from "@/components/ui/PageLoader";
import type { Package } from "@/features/deployments/api/deployments.api";

export const Route = createFileRoute("/_authenticated/apps/$appName_/deployments/$deploymentName")({
  component: DeploymentDetailPage,
});

function formatBytes(bytes?: number): string {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(ms?: number): string {
  if (!ms) return "—";
  const locale = i18n.resolvedLanguage || i18n.language || undefined;
  return new Date(ms).toLocaleString(locale);
}

function DeploymentDetailPage() {
  const { appName, deploymentName } = Route.useParams();
  const { t } = useTranslation();

  const navigate = useNavigate();
  const access = useAppAccess(appName);
  const deploymentsQuery = useDeployments(appName);
  const historyQuery = useDeploymentHistory(appName, deploymentName);
  const {
    query: metricsQuery,
    metrics: shownMetrics,
    heldSince,
    isSettling,
  } = useHeldDeploymentMetrics(appName, deploymentName);
  const haltQuery = useDeploymentHaltHistory(appName, deploymentName);
  const rollback = useRollbackDeployment(appName, deploymentName);
  const deleteDeployment = useDeleteDeployment(appName);

  const [promoteOpen, setPromoteOpen] = useState(false);
  const [rollbackTarget, setRollbackTarget] = useState<{ label: string } | null>(null);
  const [sentRollbackLabel, setSentRollbackLabel] = useState<string | null>(null);
  const [renameOpen, setRenameOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const deployment = deploymentsQuery.data?.deployments.find((d) => d.name === deploymentName);
  const otherDeployments =
    deploymentsQuery.data?.deployments
      .filter((d) => d.name !== deploymentName)
      .map((d) => d.name) ?? [];

  const history = historyQuery.data?.history ?? [];
  const newestFirst = [...history].reverse();
  const previousLabel = previousReleaseLabel(history);
  const latestRelease = newestFirst[0];
  const sentPinApplied =
    !!sentRollbackLabel && isPinnedRollbackApplied(latestRelease, sentRollbackLabel);
  const timeoutDidNotLand =
    !!sentRollbackLabel &&
    !sentPinApplied &&
    !historyQuery.isFetching &&
    isUncertainWriteTimeout(rollback.error);
  const effectiveSent = timeoutDidNotLand ? null : sentRollbackLabel;
  const pinnedRollbackApplied =
    rollbackTarget !== null && isPinnedRollbackApplied(latestRelease, rollbackTarget.label);
  const rollbackAlreadySent =
    rollbackTarget !== null && effectiveSent === rollbackTarget.label && !rollback.isPending;
  const rollbackDone = pinnedRollbackApplied || rollbackAlreadySent;
  const rollbackHaltRefused = isReleaseHalted(rollback.error);
  const rollbackSettled = rollbackDone || rollbackHaltRefused;
  const rollbackErrorMessage = rollbackHaltRefused
    ? t("deployments.rollbackHalted", { label: rollback.variables ?? rollbackTarget?.label })
    : rollback.error?.error;
  const metrics = shownMetrics ?? {};

  const rollbackHistoryChecking = rollbackAlreadySent && historyQuery.isFetching;
  const rollbackDialogPending = rollback.isPending || rollbackHistoryChecking;

  const dismissRollback = () => {
    if (rollbackDialogPending) return;
    setRollbackTarget(null);
    if (!sentRollbackLabel && !rollbackHaltRefused) rollback.reset();
  };

  const finishRollback = () => {
    setRollbackTarget(null);
    setSentRollbackLabel(null);
    rollback.reset();
  };

  const openRollback = (label: string) => {
    if (rollback.isPending) return;
    const pin = sentPinApplied && sentRollbackLabel ? sentRollbackLabel : label;
    if (sentRollbackLabel !== pin) {
      rollback.reset();
      setSentRollbackLabel(null);
    }
    setRollbackTarget({ label: pin });
  };

  const copyKey = async () => {
    if (!deployment?.key) return;
    await navigator.clipboard.writeText(deployment.key);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="mx-auto max-w-4xl">
      <div className="flex items-center gap-4">
        <Link
          to="/apps/$appName"
          params={{ appName }}
          className="flex h-9 w-9 items-center justify-center rounded-lg text-muted transition-colors hover:bg-elevated hover:text-heading"
        >
          <ArrowLeft size={18} />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-heading">{deploymentName}</h1>
          <div className="mt-0.5 flex items-center gap-3 text-sm text-muted">
            <span>{appName}</span>
            {access.can("deploymentKeys.read") &&
              deployment?.key &&
              deployment.key !== "(hidden)" && (
                <button
                  type="button"
                  onClick={copyKey}
                  className="inline-flex items-center gap-1.5 text-xs transition-colors hover:text-heading"
                  title={t("deployments.deploymentKey")}
                >
                  <Key size={10} />
                  <code className="font-mono">{deployment.key.slice(0, 16)}…</code>
                  {copied ? <Check size={10} className="text-success" /> : <Copy size={10} />}
                </button>
              )}
          </div>
        </div>
        {access.can("releases.promote") && history.length > 0 && otherDeployments.length > 0 && (
          <button
            type="button"
            onClick={() => setPromoteOpen(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo px-3.5 py-2 text-sm font-semibold text-white transition-all hover:bg-indigo-bright"
          >
            <ArrowUpRight size={14} />
            {t("deployments.promote")}
          </button>
        )}
        {access.can("releases.rollback") && (effectiveSent || previousLabel) && (
          <button
            type="button"
            onClick={() => {
              const label = effectiveSent ?? previousLabel;
              if (label) openRollback(label);
            }}
            disabled={rollback.isPending}
            className="inline-flex items-center gap-2 rounded-lg border border-border bg-surface px-3.5 py-2 text-sm font-medium text-muted transition-colors hover:border-border-light hover:text-heading disabled:opacity-50"
          >
            <Undo2 size={14} />
            {t("deployments.rollback")}
          </button>
        )}
        {access.can("deployments.manage") && (
          <>
            <button
              type="button"
              onClick={() => setRenameOpen(true)}
              title={t("deployments.renameDeployment")}
              aria-label={t("deployments.renameDeployment")}
              className="flex h-9 w-9 items-center justify-center rounded-lg text-muted transition-colors hover:bg-elevated hover:text-heading"
            >
              <Edit3 size={15} />
            </button>
            <button
              type="button"
              onClick={() => setDeleteOpen(true)}
              title={t("deployments.deleteDeployment")}
              aria-label={t("deployments.deleteDeployment")}
              className="flex h-9 w-9 items-center justify-center rounded-lg text-muted transition-colors hover:bg-danger/10 hover:text-danger"
            >
              <Trash2 size={15} />
            </button>
          </>
        )}
      </div>

      {shouldShowMetricsPanel(access) ? (
        <>
          <DeploymentMetricsPanel
            metrics={shownMetrics}
            heldSince={heldSince}
            isPending={metricsQuery.isPending || isSettling}
            isSuccess={metricsQuery.isSuccess}
            historyReady={historyQuery.data !== undefined}
            error={metricsQuery.error}
            currentRelease={newestFirst[0] ?? deployment?.package}
            history={history}
          />
          <p className="mt-2">
            <Link to="/billing" className="text-xs text-muted transition-colors hover:text-heading">
              {t("deployments.metricsBillingLink")}
            </Link>
          </p>
          <Suspense fallback={<p className="mt-4 text-sm text-muted">{t("common.loading")}</p>}>
            <DeploymentHistoryCharts
              appName={appName}
              deploymentName={deploymentName}
              history={history}
              access={access}
            />
          </Suspense>
        </>
      ) : null}

      {shouldRenderHaltHistoryPanel(
        access,
        haltQuery.error,
        haltQuery.isPending,
        (haltQuery.data?.items.length ?? 0) > 0,
      ) ? (
        <HaltHistoryPanel
          items={haltQuery.data?.items ?? []}
          isPending={haltQuery.isPending}
          isSuccess={haltQuery.isSuccess}
          error={haltQuery.error}
        />
      ) : null}

      <section className="mt-8">
        <div className="flex items-center gap-2">
          <History size={16} className="text-muted" />
          <h2 className="text-lg font-semibold text-heading">{t("deployments.releaseHistory")}</h2>
        </div>

        {(historyQuery.isLoading || deploymentsQuery.isLoading) && <PageLoader />}
        <ApiErrorBanner error={historyQuery.error} className="mt-4" />
        {((rollback.error && !effectiveSent) || deleteDeployment.error) && (
          <p role="alert" className="mt-4 text-xs text-danger">
            {(effectiveSent ? undefined : rollbackErrorMessage) ??
              deleteDeployment.error?.error ??
              t("common.error")}
          </p>
        )}

        {!historyQuery.isLoading && !historyQuery.error && newestFirst.length === 0 && (
          <p className="mt-6 text-sm text-muted">{t("deployments.noRelease")}</p>
        )}

        {newestFirst.length > 0 && (
          <div className="mt-4 space-y-3">
            {newestFirst.map((release, index) => (
              <ReleaseCard
                key={release.label ?? index}
                appName={appName}
                deploymentName={deploymentName}
                release={release}
                autoHalted={isAutoHalted(release, haltQuery.data?.items)}
                isLatest={index === 0}
                activeMetrics={release.label ? metrics[release.label] : undefined}
                heldSince={heldSince}
                onRollbackTo={openRollback}
                rollbackPending={rollback.isPending}
                access={access}
              />
            ))}
          </div>
        )}
      </section>

      <PromoteDialog
        appName={appName}
        sourceDeploymentName={deploymentName}
        sourceDisabled={latestRelease?.isDisabled === true}
        destinations={otherDeployments}
        open={promoteOpen}
        onClose={() => setPromoteOpen(false)}
      />

      <RenameDeploymentDialog
        appName={appName}
        deploymentName={deploymentName}
        open={renameOpen}
        onClose={() => setRenameOpen(false)}
      />

      <ConfirmDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={() => {
          deleteDeployment.mutate(deploymentName, {
            onSuccess: () => navigate({ to: "/apps/$appName", params: { appName }, replace: true }),
          });
        }}
        title={t("deployments.deleteDeployment")}
        description={t("deployments.deleteDeploymentWarning", { deployment: deploymentName })}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        destructive
        isPending={deleteDeployment.isPending}
        closeOnConfirm={false}
        error={deleteDeployment.error?.error}
      />

      <ConfirmDialog
        open={rollbackTarget !== null}
        onClose={dismissRollback}
        onConfirm={() => {
          if (!rollbackTarget) return;
          if (rollbackDone) {
            if (pinnedRollbackApplied) finishRollback();
            else dismissRollback();
            return;
          }
          if (rollbackHaltRefused) {
            dismissRollback();
            return;
          }
          setSentRollbackLabel(rollbackTarget.label);
          rollback.mutate(rollbackTarget.label, {
            onSuccess: finishRollback,
            onError: (error) => {
              if (!isUncertainWriteTimeout(error)) setSentRollbackLabel(null);
            },
          });
        }}
        title={t("deployments.rollback")}
        description={
          pinnedRollbackApplied
            ? t("deployments.rollbackAlreadyApplied", {
                label: latestRelease?.label ?? rollbackTarget?.label,
              })
            : rollbackAlreadySent
              ? t("deployments.rollbackChecking")
              : rollbackTarget && !rollbackHaltRefused
                ? t("deployments.rollbackToConfirm", {
                    deployment: deploymentName,
                    label: rollbackTarget.label,
                  })
                : ""
        }
        confirmLabel={rollbackSettled ? t("common.close") : t("deployments.rollback")}
        cancelLabel={t("common.cancel")}
        destructive={!rollbackSettled}
        isPending={rollbackDialogPending}
        closeOnConfirm={false}
        hideCancel={rollbackSettled}
        error={rollbackDone ? undefined : rollbackErrorMessage}
      />
    </div>
  );
}

interface ReleaseCardProps {
  appName: string;
  deploymentName: string;
  release: Package;
  autoHalted: boolean;
  isLatest: boolean;
  activeMetrics?: { active: number; downloaded?: number; failed?: number; installed?: number };
  heldSince: number | undefined;
  onRollbackTo: (label: string) => void;
  rollbackPending: boolean;
  access: AppAccess;
}

function ReleaseCard({
  appName,
  deploymentName,
  release,
  autoHalted,
  isLatest,
  activeMetrics,
  heldSince,
  onRollbackTo,
  rollbackPending,
  access,
}: ReleaseCardProps) {
  const { t } = useTranslation();
  const [editOpen, setEditOpen] = useState(false);

  return (
    <div
      className={`rounded-xl border bg-surface p-5 transition-all hover:border-border-light ${
        isLatest ? "border-indigo/30" : "border-border"
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-sm font-semibold text-heading">{release.label}</span>
          {isLatest && (
            <span className="rounded-full bg-indigo/10 px-2 py-0.5 text-xs font-medium text-indigo">
              {t("deployments.latest")}
            </span>
          )}
          {release.isMandatory && (
            <span className="rounded-full bg-warning/10 px-2 py-0.5 text-xs font-medium text-warning">
              {t("deployments.mandatory")}
            </span>
          )}
          {release.isDisabled && (
            <span className="rounded-full bg-danger/10 px-2 py-0.5 text-xs font-medium text-danger">
              {t("deployments.disabled")}
            </span>
          )}
          {release.rollout != null && release.rollout !== 100 && (
            <span className="rounded-full bg-elevated px-2 py-0.5 text-xs font-medium text-muted">
              {t("deployments.rolloutBadge", { percent: release.rollout })}
            </span>
          )}
          {release.releaseMethod === "Promote" && release.originalDeployment && (
            <span className="text-xs text-muted">
              {t("deployments.promotedFrom", {
                deployment: release.originalDeployment,
                label: release.originalLabel ?? "",
              })}
            </span>
          )}
          {release.releaseMethod === "Rollback" && release.originalLabel && (
            <span className="text-xs text-muted">
              {t("deployments.rollbackOf", { label: release.originalLabel })}
            </span>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {access.can("releases.deploy") && (
            <button
              type="button"
              onClick={() => setEditOpen(true)}
              title={t("deployments.editRelease")}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-muted transition-colors hover:bg-elevated hover:text-heading"
            >
              <Pencil size={14} />
            </button>
          )}
          {access.can("releases.rollback") && !isLatest && release.label && (
            <button
              type="button"
              onClick={() => onRollbackTo(release.label!)}
              disabled={rollbackPending}
              title={t("deployments.rollbackToThis")}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-muted transition-colors hover:bg-elevated hover:text-heading disabled:opacity-50"
            >
              <Undo2 size={14} />
            </button>
          )}
        </div>
      </div>

      {release.description && <p className="mt-3 text-sm text-muted">{release.description}</p>}

      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted">
        <span>v{release.appVersion ?? "?"}</span>
        <span>{formatBytes(release.size)}</span>
        <span>{formatDate(release.uploadTime)}</span>
        {activeMetrics && (
          <span className={heldSince === undefined ? "text-heading" : "text-muted"}>
            {t("deployments.metricsSummary", {
              active: activeMetrics.active,
              downloaded: activeMetrics.downloaded ?? 0,
              failed: activeMetrics.failed ?? 0,
            })}
            {heldSince === undefined
              ? null
              : ` · ${t("deployments.metricsHeldAge", { time: formatRelativeTime(heldSince) })}`}
          </span>
        )}
      </div>

      {editOpen && (
        <EditReleaseDialog
          appName={appName}
          deploymentName={deploymentName}
          release={release}
          autoHalted={autoHalted}
          open={editOpen}
          onClose={() => setEditOpen(false)}
        />
      )}
    </div>
  );
}
